import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, CircleMarker, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.heat';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { Box, Typography, LinearProgress, TextField, InputAdornment, IconButton, Tooltip, Button, Switch, FormControlLabel, Popover } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import RestoreIcon from '@mui/icons-material/Restore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useRegisterUnsavedSession } from '../contexts/UnsavedSessionContext';
import 'leaflet/dist/leaflet.css';

const DEFAULT_CENTER = [19.43, -99.13]; // Mexico City
const DEFAULT_ZOOM = 5;
const MIN_RADIUS = 6;
const MAX_RADIUS = 24;
const DEFAULT_MARKER_COLOR = '#1976d2';
const MARKER_WEIGHT = 2;
const MARKER_OPACITY = 0.7;
const MARKER_FILL_OPACITY = 0.35;

const SIZE_SCALE = { small: 0.65, medium: 1, large: 1.4 };

/** Interpola entre dos colores hex; t en [0,1]. */
function interpolateColor(hexMin, hexMax, t) {
  const parse = (hex) => {
    const h = hex.replace(/^#/, '');
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  };
  const [r1, g1, b1] = parse(hexMin || '#2196f3');
  const [r2, g2, b2] = parse(hexMax || '#f44336');
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}

function createPinIcon(color, sizeKey) {
  const scale = SIZE_SCALE[sizeKey] || 1;
  const w = Math.round(24 * scale);
  const h = Math.round(36 * scale);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 24 36">
    <path fill="${color}" stroke="#fff" stroke-width="2" d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z"/>
  </svg>`;
  return L.divIcon({
    html: svg,
    className: 'custom-marker-pin',
    iconSize: [w, h],
    iconAnchor: [w / 2, h],
  });
}

function createSquareIcon(color, sizeKey) {
  const scale = SIZE_SCALE[sizeKey] || 1;
  const s = Math.round(16 * scale);
  return L.divIcon({
    html: `<div style="width:${s}px;height:${s}px;background:${color};border:2px solid #fff;border-radius:2px;box-shadow:0 1px 3px rgba(0,0,0,0.3);"></div>`,
    className: 'custom-marker-square',
    iconSize: [s, s],
    iconAnchor: [s / 2, s / 2],
  });
}

function createCircleIcon(color, sizeKey) {
  const scale = SIZE_SCALE[sizeKey] || 1;
  const r = Math.round((MIN_RADIUS + MAX_RADIUS) / 2 * scale);
  const s = r * 2;
  return L.divIcon({
    html: `<div style="width:${s}px;height:${s}px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.3);opacity:0.7;"></div>`,
    className: 'custom-marker-circle',
    iconSize: [s, s],
    iconAnchor: [r, r],
  });
}

// Cache y throttle para Nominatim (máx 1 req/seg según política OSM)
const geocodeCache = new Map();
let lastGeocodeTime = 0;
const NOMINATIM_DELAY_MS = 1100;

function buildAddressQuery(address, comuna, region, country) {
  const parts = [address, comuna, region, country].filter(Boolean).map((s) => String(s).trim()).filter(Boolean);
  return parts.join(', ');
}

function nominatimGeocode(addressString) {
  const key = addressString.trim().toLowerCase();
  if (!key) return Promise.resolve(null);
  const cached = geocodeCache.get(key);
  if (cached) return Promise.resolve(cached);

  return new Promise((resolve) => {
    const now = Date.now();
    const elapsed = now - lastGeocodeTime;
    const wait = Math.max(0, NOMINATIM_DELAY_MS - elapsed);
    lastGeocodeTime = now + wait;

    setTimeout(() => {
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(addressString)}&format=json&limit=1`;
      fetch(url, { headers: { Accept: 'application/json' } })
        .then((res) => res.json())
        .then((arr) => {
          const first = Array.isArray(arr) && arr[0];
          if (first && first.lat != null && first.lon != null) {
            const result = { lat: parseFloat(first.lat), lng: parseFloat(first.lon) };
            geocodeCache.set(key, result);
            resolve(result);
          } else {
            geocodeCache.set(key, null);
            resolve(null);
          }
        })
        .catch(() => {
          geocodeCache.set(key, null);
          resolve(null);
        });
    }, wait);
  });
}

/**
 * Geocodifica en secuencia (1 req/seg). onProgress(done, total) se llama tras cada una.
 * resultsPre: Map opcional con resultados ya conocidos (p. ej. desde caché BD).
 * onSave(key, coords): opcional; se llama solo cuando el resultado es nuevo (no estaba en resultsPre), para guardar en caché (ej. API).
 */
async function geocodeAddresses(uniqueQueries, onProgress, { resultsPre = null, onSave = null } = {}) {
  const results = resultsPre ? new Map(resultsPre) : new Map();
  const total = uniqueQueries.length;
  for (let i = 0; i < uniqueQueries.length; i++) {
    const key = uniqueQueries[i];
    const coords = await nominatimGeocode(key);
    const wasNew = !resultsPre || !resultsPre.has(key);
    if (coords) {
      results.set(key, coords);
      if (wasNew && typeof onSave === 'function') onSave(key.trim().toLowerCase(), coords);
    } else {
      if (wasNew && typeof onSave === 'function') onSave(key.trim().toLowerCase(), null);
    }
    if (typeof onProgress === 'function') onProgress(i + 1, total);
  }
  return results;
}

// Capa de mapa de calor usando leaflet.heat
function HeatmapLayer({ points, useValueForIntensity, radius = 25, blur = 15 }) {
  const map = useMap();

  useEffect(() => {
    if (!map || !points || points.length === 0 || !L.heatLayer) return;
    const heatPoints = points
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng))
      .map((p) => {
        const weight = useValueForIntensity && p.value != null && Number.isFinite(p.value) ? p.value : 1;
        return [p.lat, p.lng, weight];
      });
    if (heatPoints.length === 0) return;

    const layer = L.heatLayer(heatPoints, {
      radius,
      blur,
      maxZoom: 17,
    }).addTo(map);

    return () => {
      if (map && layer) {
        map.removeLayer(layer);
      }
    };
  }, [map, points, useValueForIntensity, radius, blur]);

  return null;
}

// Ajusta la vista del mapa para incluir todos los puntos cuando hay datos
// y vuelve a ajustar al hacer resize del contenedor del mapa.
function FitBounds({ points }) {
  const map = useMap();
  const bounds = useMemo(() => {
    if (!points || points.length === 0) return null;
    const lats = points.map((p) => p[0]).filter((n) => Number.isFinite(n));
    const lngs = points.map((p) => p[1]).filter((n) => Number.isFinite(n));
    if (lats.length === 0 || lngs.length === 0) return null;
    const pad = 0.05;
    return [
      [Math.min(...lats) - pad, Math.min(...lngs) - pad],
      [Math.max(...lats) + pad, Math.max(...lngs) + pad],
    ];
  }, [points]);

  React.useEffect(() => {
    if (bounds && map) {
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 14 });
    }
  }, [map, bounds]);

  // Re-aplicar bounds cuando el mapa cambie de tamaño (resize del gráfico/contenedor)
  React.useEffect(() => {
    if (!map || typeof ResizeObserver === 'undefined') return;
    const container = map.getContainer ? map.getContainer() : map._container;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      map.invalidateSize();
      if (bounds) {
        map.fitBounds(bounds, { padding: [30, 30], maxZoom: 14 });
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, [map, bounds]);

  return null;
}

const MapWidget = ({
  data,
  mapConfig,
  getAuthHeaders,
  onMapConfigChange,
  forceHeatmapMode = false,
  hideHeatmapToggle = false,
  compactControls = false,
  unsavedSessionId,
}) => {
  const config = mapConfig || {};
  const latColumn = config.latColumn || '';
  const lngColumn = config.lngColumn || '';
  const addressColumn = config.addressColumn || '';
  const comunaColumn = config.comunaColumn || '';
  const regionColumn = config.regionColumn || '';
  const countryColumn = config.countryColumn || '';
  const labelColumn = config.labelColumn || '';
  const valueColumn = config.valueColumn || '';
  const valueMapping = config.valueMapping || 'size'; // 'size' | 'color' | 'both'
  const valueColorMin = config.valueColorMin || '#2196f3';
  const valueColorMax = config.valueColorMax || '#f44336';
  const popupColumns = Array.isArray(config.popupColumns) ? config.popupColumns : [];
  const markerStyle = config.markerStyle || 'circle';
  const markerColor = config.markerColor || DEFAULT_MARKER_COLOR;
  const markerSize = config.markerSize || 'medium';
  const useGeocoding = !latColumn && !lngColumn && addressColumn;

  const geocodeLookupMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/geocode/lookup', body, { headers }),
  });
  const geocodeSaveMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/geocode/save', body, { headers }),
  });
  const geocodeOverrideMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/geocode/override', body, { headers }),
  });
  const geocodeResetMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/geocode/reset', body, { headers }),
  });

  const [geocodeResults, setGeocodeResults] = useState(null);
  const [geocodeLoading, setGeocodeLoading] = useState(false);
  const [geocodeProgress, setGeocodeProgress] = useState({ current: 0, total: 0, fromCache: 0, fromApi: 0 });
  const [geocodeError, setGeocodeError] = useState(null);

  const uniqueAddressQueries = useMemo(() => {
    if (!useGeocoding || !data?.length || !addressColumn) return [];
    const set = new Set();
    data.forEach((row) => {
      const address = row[addressColumn] != null && row[addressColumn] !== '' ? String(row[addressColumn]).trim() : '';
      const comuna = comunaColumn && (row[comunaColumn] != null && row[comunaColumn] !== '') ? String(row[comunaColumn]).trim() : '';
      const region = regionColumn && (row[regionColumn] != null && row[regionColumn] !== '') ? String(row[regionColumn]).trim() : '';
      const country = countryColumn && (row[countryColumn] != null && row[countryColumn] !== '') ? String(row[countryColumn]).trim() : '';
      const q = buildAddressQuery(address, comuna, region, country);
      if (q) set.add(q);
    });
    return Array.from(set);
  }, [useGeocoding, data, addressColumn, comunaColumn, regionColumn, countryColumn]);

  useEffect(() => {
    if (!useGeocoding || uniqueAddressQueries.length === 0) {
      setGeocodeResults(null);
      setGeocodeLoading(false);
      setGeocodeProgress({ current: 0, total: 0, fromCache: 0, fromApi: 0 });
      return;
    }
    let cancelled = false;
    setGeocodeLoading(true);
    setGeocodeError(null);
    setGeocodeProgress({ current: 0, total: uniqueAddressQueries.length, fromCache: 0, fromApi: 0 });

    (async () => {
      const queries = uniqueAddressQueries;
      const headers = typeof getAuthHeaders === 'function' ? getAuthHeaders() : {};
      const hasAuth = headers && headers.Authorization;

      // Cargar caché desde appdata (solo si hay token para no disparar 401)
      if (hasAuth && queries.length > 0) {
        try {
          const res = await geocodeLookupMutation.mutateAsync({ body: { addresses: queries }, headers });
          if (!cancelled && res.data?.success && res.data.cache) {
            Object.entries(res.data.cache).forEach(([k, v]) => geocodeCache.set(k, v ?? null));
          }
        } catch (_) {}
      }
      if (cancelled) return;

      const total = queries.length;
      const resultsPre = new Map();
      queries.forEach((q) => {
        const c = geocodeCache.get(q.trim().toLowerCase());
        if (c) resultsPre.set(q, c);
      });
      if (!cancelled) {
        setGeocodeResults(resultsPre);
      }
      const toGeocode = queries.filter((q) => !geocodeCache.has(q.trim().toLowerCase()));
      if (toGeocode.length === 0) {
        if (!cancelled) {
          setGeocodeResults(resultsPre);
          setGeocodeLoading(false);
          setGeocodeProgress({ current: total, total, fromCache: total, fromApi: 0 });
        }
        return;
      }
      if (!cancelled) {
        setGeocodeProgress({
          current: resultsPre.size,
          total,
          fromCache: resultsPre.size,
          fromApi: toGeocode.length,
        });
      }

      const onSave = hasAuth
        ? (key, coords) => {
            const lat = coords && Number.isFinite(coords.lat) ? coords.lat : null;
            const lng = coords && Number.isFinite(coords.lng) ? coords.lng : null;
            geocodeSaveMutation.mutateAsync({ body: { address: key, lat, lng }, headers }).catch(() => {});
          }
        : null;

      // Iterar solo las que faltan para que el progreso no baje (72/98 -> 73/98 ... 98/98)
      try {
        const map = await geocodeAddresses(toGeocode, (done) => {
          if (!cancelled) {
            setGeocodeProgress((prev) => ({
              ...prev,
              current: resultsPre.size + done,
            }));
          }
        }, { resultsPre, onSave });
        if (!cancelled) {
          setGeocodeResults(map);
          setGeocodeLoading(false);
          setGeocodeProgress((p) => ({ ...p, current: p.total }));
        }
      } catch {
        if (!cancelled) {
          setGeocodeError('Error al geocodificar direcciones');
          setGeocodeLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [useGeocoding, uniqueAddressQueries.join('|')]);

  const points = useMemo(() => {
    if (!data?.length) return [];

    if (latColumn && lngColumn) {
      const out = [];
      let minVal = Infinity;
      let maxVal = -Infinity;
      for (const row of data) {
        const lat = parseFloat(row[latColumn]);
        const lng = parseFloat(row[lngColumn]);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
        out.push({
          lat,
          lng,
          label: labelColumn && (row[labelColumn] != null && row[labelColumn] !== '') ? String(row[labelColumn]) : null,
          value: valueColumn && (row[valueColumn] != null && row[valueColumn] !== '') ? parseFloat(row[valueColumn]) : null,
          rowData: row,
        });
        if (out[out.length - 1].value != null && Number.isFinite(out[out.length - 1].value)) {
          minVal = Math.min(minVal, out[out.length - 1].value);
          maxVal = Math.max(maxVal, out[out.length - 1].value);
        }
      }
      const scale = SIZE_SCALE[markerSize] || 1;
      const span = maxVal - minVal || 1;
      out.forEach((p) => {
        const hasVal = p.value != null && Number.isFinite(p.value);
        const t = hasVal && span > 0 ? (p.value - minVal) / span : 0.5;
        if (valueMapping === 'size' || valueMapping === 'both') {
          if (hasVal) p.radius = (MIN_RADIUS + t * (MAX_RADIUS - MIN_RADIUS)) * scale;
          else p.radius = ((MIN_RADIUS + MAX_RADIUS) / 2) * scale;
        } else {
          p.radius = ((MIN_RADIUS + MAX_RADIUS) / 2) * scale;
        }
        if (valueMapping === 'color' || valueMapping === 'both') {
          p.color = hasVal ? interpolateColor(valueColorMin, valueColorMax, t) : markerColor;
        } else {
          p.color = markerColor;
        }
      });
      return out;
    }

    if (useGeocoding && geocodeResults && !geocodeLoading) {
      const out = [];
      let minVal = Infinity;
      let maxVal = -Infinity;
      for (const row of data) {
        const address = row[addressColumn] != null && row[addressColumn] !== '' ? String(row[addressColumn]).trim() : '';
        const comuna = comunaColumn && (row[comunaColumn] != null && row[comunaColumn] !== '') ? String(row[comunaColumn]).trim() : '';
        const region = regionColumn && (row[regionColumn] != null && row[regionColumn] !== '') ? String(row[regionColumn]).trim() : '';
        const country = countryColumn && (row[countryColumn] != null && row[countryColumn] !== '') ? String(row[countryColumn]).trim() : '';
        const q = buildAddressQuery(address, comuna, region, country);
        if (!q) continue;
        const coords = geocodeResults.get(q);
        if (!coords) continue;
        out.push({
          lat: coords.lat,
          lng: coords.lng,
          label: labelColumn && (row[labelColumn] != null && row[labelColumn] !== '') ? String(row[labelColumn]) : null,
          value: valueColumn && (row[valueColumn] != null && row[valueColumn] !== '') ? parseFloat(row[valueColumn]) : null,
          rowData: row,
          addressKey: q.trim().toLowerCase(),
          addressQuery: q,
          overridden: !!(coords && coords.overridden),
        });
        if (out[out.length - 1].value != null && Number.isFinite(out[out.length - 1].value)) {
          minVal = Math.min(minVal, out[out.length - 1].value);
          maxVal = Math.max(maxVal, out[out.length - 1].value);
        }
      }
      const scale = SIZE_SCALE[markerSize] || 1;
      const span = maxVal - minVal || 1;
      out.forEach((p) => {
        const hasVal = p.value != null && Number.isFinite(p.value);
        const t = hasVal && span > 0 ? (p.value - minVal) / span : 0.5;
        if (valueMapping === 'size' || valueMapping === 'both') {
          if (hasVal) p.radius = (MIN_RADIUS + t * (MAX_RADIUS - MIN_RADIUS)) * scale;
          else p.radius = ((MIN_RADIUS + MAX_RADIUS) / 2) * scale;
        } else {
          p.radius = ((MIN_RADIUS + MAX_RADIUS) / 2) * scale;
        }
        if (valueMapping === 'color' || valueMapping === 'both') {
          p.color = hasVal ? interpolateColor(valueColorMin, valueColorMax, t) : markerColor;
        } else {
          p.color = markerColor;
        }
      });
      return out;
    }

    return [];
  }, [data, latColumn, lngColumn, useGeocoding, geocodeResults, geocodeLoading, addressColumn, comunaColumn, regionColumn, countryColumn, labelColumn, valueColumn, valueMapping, valueColorMin, valueColorMax, popupColumns, markerSize, markerStyle, markerColor]);

  const [searchQuery, setSearchQuery] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [pendingOverrides, setPendingOverrides] = useState({});
  const [heatmapMode, setHeatmapMode] = useState(forceHeatmapMode || !!config.heatmapMode);

  useEffect(() => {
    if (forceHeatmapMode) setHeatmapMode(true);
  }, [forceHeatmapMode]);
  const [infoAnchor, setInfoAnchor] = useState(null);

  const filteredPoints = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return points;
    return points.filter((p) => {
      const parts = [];
      if (p.rowData) {
        Object.keys(p.rowData).forEach((col) => {
          const v = p.rowData[col];
          if (v != null && v !== '') parts.push(String(v).toLowerCase());
        });
      }
      if (p.label != null && p.label !== '') parts.push(String(p.label).toLowerCase());
      const text = parts.join(' ');
      return text.includes(q);
    });
  }, [points, searchQuery]);

  const pointsToShow = useMemo(() => {
    return filteredPoints.map((p) => {
      const override = p.addressKey && pendingOverrides[p.addressKey];
      if (override) return { ...p, lat: override.lat, lng: override.lng };
      return p;
    });
  }, [filteredPoints, pendingOverrides]);

  const boundsPoints = useMemo(() => pointsToShow.map((p) => [p.lat, p.lng]), [pointsToShow]);

  const hasEditableMarkers = useMemo(
    () => points.some((p) => !!p.addressKey && !!(typeof getAuthHeaders === 'function' && getAuthHeaders().Authorization)),
    [points]
  );

  const mapStats = useMemo(() => {
    const total = points.length;
    const withValue = points.filter((p) => p.value != null && Number.isFinite(p.value));
    if (withValue.length === 0) return { total, minVal: null, maxVal: null };
    const values = withValue.map((p) => Number(p.value));
    return { total, minVal: Math.min(...values), maxVal: Math.max(...values) };
  }, [points]);

  const handleOverride = (addressQuery, lat, lng) => {
    const headers = typeof getAuthHeaders === 'function' ? getAuthHeaders() : {};
    if (!headers || !headers.Authorization) return;
    geocodeOverrideMutation
      .mutateAsync({ body: { address: addressQuery.trim().toLowerCase(), lat, lng }, headers })
      .then(() => {
        setGeocodeResults((prev) => {
          const next = new Map(prev);
          const cur = next.get(addressQuery);
          if (cur) next.set(addressQuery, { ...cur, lat, lng, overridden: true });
          return next;
        });
      })
      .catch(() => {});
  };

  const handleReset = (addressQuery) => {
    const headers = typeof getAuthHeaders === 'function' ? getAuthHeaders() : {};
    if (!headers || !headers.Authorization) return;
    geocodeResetMutation
      .mutateAsync({ body: { address: addressQuery.trim().toLowerCase() }, headers })
      .then((res) => {
        if (res.data && res.data.lat != null && res.data.lng != null) {
          setGeocodeResults((prev) => {
            const next = new Map(prev);
            next.set(addressQuery, { lat: res.data.lat, lng: res.data.lng, overridden: false });
            return next;
          });
        }
      })
      .catch(() => {});
  };

  const cancelEdit = useCallback(() => {
    setPendingOverrides({});
    setEditMode(false);
  }, []);

  const confirmEdit = useCallback(() => {
    const list = Object.values(pendingOverrides);
    if (list.length === 0) {
      setEditMode(false);
      return;
    }
    list.forEach(({ addressQuery, lat, lng }) => handleOverride(addressQuery, lat, lng));
    setGeocodeResults((prev) => {
      const next = new Map(prev);
      list.forEach(({ addressQuery, lat, lng }) => {
        const cur = next.get(addressQuery);
        if (cur) next.set(addressQuery, { ...cur, lat, lng, overridden: true });
      });
      return next;
    });
    setPendingOverrides({});
    setEditMode(false);
  }, [pendingOverrides]);

  useRegisterUnsavedSession(unsavedSessionId, editMode, {
    dirty: Object.keys(pendingOverrides).length > 0,
    message:
      'Se están editando las posiciones de los marcadores del mapa. Guarda o descarta los cambios para continuar.',
    save: confirmEdit,
    discard: cancelEdit,
  });

  const hasCoordMode = latColumn && lngColumn;
  const hasAddressMode = addressColumn;

  if (!hasCoordMode && !hasAddressMode) {
    return (
      <Box sx={{ height: '100%', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">Configura columnas de lat/lng o de dirección y comuna.</Typography>
      </Box>
    );
  }

  if (useGeocoding && !geocodeResults && (geocodeLoading || geocodeError)) {
    const total = geocodeProgress.total || uniqueAddressQueries.length;
    const current = geocodeProgress.current || 0;
    const fromCache = geocodeProgress.fromCache || 0;
    const totalApi = Math.max(total - fromCache, 0);
    const doneApi = Math.max(current - fromCache, 0);
    const remainingApi = Math.max(totalApi - doneApi, 0);
    const pct = total > 0 ? Math.round((current / total) * 100) : 0;
    return (
      <Box sx={{ height: '100%', minHeight: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, px: 2 }}>
        <Typography color="text.secondary" textAlign="center">
          {geocodeLoading
            ? totalApi > 0
              ? `Preparando mapa: ${current}/${total} dirección(es) listas…`
              : 'Cargando coordenadas desde la caché…'
            : geocodeError}
        </Typography>
        {geocodeLoading && total > 0 && (
          <Box sx={{ width: '100%', maxWidth: 320 }}>
            <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 1 }} />
          </Box>
        )}
        {geocodeLoading && (
          <Typography variant="caption" color="text.secondary">
            {`En caché: ${fromCache} / ${total} · A geocodificar vía API: ${remainingApi} (~1 dirección/seg por límite del servicio)`}
          </Typography>
        )}
      </Box>
    );
  }

  if (points.length === 0) {
    return (
      <Box sx={{ height: '100%', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">
          {useGeocoding ? 'No se encontraron coordenadas para las direcciones.' : 'No hay datos con coordenadas válidas para mostrar.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 200,
        position: 'relative',
        borderRadius: 1,
        overflow: 'hidden',
        '& .leaflet-marker-icon.custom-marker-pin': { background: 'transparent !important', border: 'none !important' },
        '& .leaflet-marker-icon.custom-marker-square': { background: 'transparent !important', border: 'none !important' },
        '& .leaflet-marker-icon.custom-marker-circle': { background: 'transparent !important', border: 'none !important' },
      }}
    >
      {editMode ? (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            right: 8,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            p: 1,
            borderRadius: 1,
            bgcolor: 'background.paper',
            boxShadow: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Editando posiciones. Arrastra los marcadores y pulsa Confirmar para guardar.
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button size="small" variant="outlined" onClick={cancelEdit}>
              Cancelar
            </Button>
            <Button size="small" variant="contained" onClick={confirmEdit}>
              Confirmar posición
            </Button>
          </Box>
        </Box>
      ) : !compactControls ? (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            left: 'auto',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          <TextField
            size="small"
            placeholder="Buscar en popup o dirección (mín. 1 carácter)…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 1,
              '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' },
              minWidth: 200,
              maxWidth: 320,
            }}
          />
          {hasEditableMarkers && (
            <Tooltip title="Editar posiciones de los marcadores">
              <IconButton size="small" onClick={() => setEditMode(true)} sx={{ bgcolor: 'background.paper' }}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
          {!hideHeatmapToggle && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={heatmapMode}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setHeatmapMode(checked);
                    if (typeof onMapConfigChange === 'function') {
                      onMapConfigChange({ heatmapMode: checked });
                    }
                  }}
                />
              }
              label="Mapa de calor"
              sx={{
                ml: 1,
                bgcolor: 'background.paper',
                borderRadius: 1,
                pl: 1,
              }}
            />
          )}
          {searchQuery.trim() !== '' && (
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              {filteredPoints.length} de {points.length}
            </Typography>
          )}
          <Tooltip title="Ver información del mapa">
            <IconButton
              size="small"
              onClick={(e) => setInfoAnchor(e.currentTarget)}
              sx={{ bgcolor: 'background.paper' }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ) : null}
      <Popover
        open={!!infoAnchor}
        anchorEl={infoAnchor}
        onClose={() => setInfoAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, minWidth: 220 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Información del mapa
          </Typography>
          <Typography variant="body2">
            Total de elementos: {mapStats.total.toLocaleString()}
          </Typography>
          {valueColumn && mapStats.minVal != null && mapStats.maxVal != null && (
            <>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {valueColumn} — mínimo: {Number(mapStats.minVal).toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </Typography>
              <Typography variant="body2">
                {valueColumn} — máximo: {Number(mapStats.maxVal).toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </Typography>
            </>
          )}
        </Box>
      </Popover>
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        style={{ height: '100%', width: '100%', minHeight: 200 }}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {boundsPoints.length > 0 && config.fitBoundsOnLoad !== false && <FitBounds points={boundsPoints} />}
        {heatmapMode && (
          <HeatmapLayer
            points={pointsToShow}
            useValueForIntensity={!!config.heatmapUseValue}
            radius={config.heatmapRadius != null ? config.heatmapRadius : 25}
            blur={config.heatmapBlur != null ? config.heatmapBlur : 15}
          />
        )}
        {!heatmapMode && pointsToShow.map((p, i) => {
          const canEdit = !!p.addressKey && !!(typeof getAuthHeaders === 'function' && getAuthHeaders().Authorization);
          const markerColorUse = p.color != null ? p.color : markerColor;
          const icon = markerStyle === 'pin'
            ? createPinIcon(markerColorUse, markerSize)
            : markerStyle === 'square'
              ? createSquareIcon(markerColorUse, markerSize)
              : createCircleIcon(markerColorUse, markerSize);
          const popupContent = (
            <Popup>
              {popupColumns.length > 0 && p.rowData ? (
                popupColumns.map((col) => {
                  const val = p.rowData[col];
                  const display = val == null ? '' : typeof val === 'number' && Number.isFinite(val)
                    ? val.toLocaleString(undefined, { maximumFractionDigits: 2 })
                    : String(val);
                  return display !== '' ? <div key={col} style={{ marginBottom: 4 }}><strong>{col}</strong><br />{display}</div> : null;
                }).filter(Boolean)
              ) : (
                <>
                  {p.label != null && p.label !== '' && <div><strong>{p.label}</strong></div>}
                  {p.value != null && Number.isFinite(p.value) && (
                    <div>{valueColumn}: {Number(p.value).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                  )}
                  {(!p.label || p.label === '') && (p.value == null || !Number.isFinite(p.value)) && (
                    <div>{p.lat.toFixed(4)}, {p.lng.toFixed(4)}</div>
                  )}
                </>
              )}
              {canEdit && editMode && (
                <Box sx={{ display: 'flex', gap: 0.25, mt: 0.5, alignItems: 'center' }}>
                  <Tooltip title={p.overridden ? 'Restaurar posición de la API' : 'Sin cambios (posición original)'}>
                    <span>
                      <IconButton
                        size="small"
                        sx={{ p: 0.25, minWidth: 0 }}
                        disabled={!p.overridden}
                        onClick={() => p.addressQuery && handleReset(p.addressQuery)}
                      >
                        <RestoreIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              )}
            </Popup>
          );
          if (canEdit && editMode) {
            return (
              <Marker
                key={i}
                position={[p.lat, p.lng]}
                icon={icon}
                draggable
                eventHandlers={{
                  dragend: (e) => {
                    const pos = e.target.getLatLng();
                    if (p.addressKey && p.addressQuery) {
                      setPendingOverrides((prev) => ({
                        ...prev,
                        [p.addressKey]: { addressQuery: p.addressQuery, lat: pos.lat, lng: pos.lng },
                      }));
                    }
                  },
                }}
              >
                {popupContent}
              </Marker>
            );
          }
          if (canEdit) {
            return (
              <Marker key={i} position={[p.lat, p.lng]} icon={icon}>
                {popupContent}
              </Marker>
            );
          }
          if (markerStyle === 'pin') {
            return (
              <Marker key={i} position={[p.lat, p.lng]} icon={createPinIcon(markerColorUse, markerSize)}>
                {popupContent}
              </Marker>
            );
          }
          if (markerStyle === 'square') {
            return (
              <Marker key={i} position={[p.lat, p.lng]} icon={createSquareIcon(markerColorUse, markerSize)}>
                {popupContent}
              </Marker>
            );
          }
          return (
            <CircleMarker
              key={i}
              center={[p.lat, p.lng]}
              radius={p.radius}
              pathOptions={{
                color: markerColorUse,
                weight: MARKER_WEIGHT,
                opacity: MARKER_OPACITY,
                fillColor: markerColorUse,
                fillOpacity: MARKER_FILL_OPACITY,
              }}
            >
              {popupContent}
            </CircleMarker>
          );
        })}
      </MapContainer>
    </Box>
  );
};

export default MapWidget;
